import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { MinusIcon, PlusIcon, XIcon, RepeatIcon } from 'lucide-react';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { ProductImage } from '../../ui/ProductImage';
import { productMeta } from '../../utils/catalog';
import { isSoldOut, maxQty } from '../../core/rules/stock';
import { canSubscribe } from '../../core/rules/features';
import { useFeatures } from '../../hooks/useFeatures';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { ease } from '../../core/format/motion';
import type { CartLine as Line } from '../../contexts/StoreContext';

/** 40px raakvlak: de mand wordt vooral op de telefoon gebruikt. */
const stepBtn = 'flex h-10 w-10 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink';

/** Eén regel in de mand: product, aantal, verwijderen en (bij navulbaar) automatisch bijvullen. */
export function CartLine({
  line
}: {
  line: Line;
}) {
  const {
    setQty,
    remove,
    toggleSubscribe
  } = useCartActions();
  const {
    closeCart
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const features = useFeatures();
  const {
    product,
    qty,
    subscribe,
    routineId
  } = line;
  const meta = productMeta(product);
  const soldOut = isSoldOut(product);
  return <motion.li layout={!reduce} initial={reduce ? false : {
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    x: 24,
    transition: {
      duration: 0.18,
      ease
    }
  }} transition={{
    duration: 0.24,
    ease
  }} className="py-5" data-id="element-6722" data-mp="3kzf5goolg3vg">
      <div className="flex gap-4" data-id="element-6723" data-mp="25idelsrt5bqd">
        {/* Een kuur heeft geen productpagina; hij staat bij de behandelingen. */}
        <Link to={product.kind === 'kuur' ? '/behandelingen' : `/webshop/${product.id}`} onClick={closeCart} className="shrink-0" data-id="element-6724" data-mp="20kbvj7udtcew">
          <ProductImage src={product.image} alt={shopLabels.productImage(product)} className="h-24 w-20 shrink-0 p-2" data-id="element-6725" data-mp="2q9ab6ngz4k1u" />
        </Link>
        <div className="flex flex-1 flex-col" data-id="element-6726" data-mp="2ub458cwnypt1">
          <div className="flex items-start justify-between gap-3" data-id="element-6727" data-mp="2z7ckrrerwl0q">
            <div data-id="element-6728" data-mp="3477b9tumz8l9">
              <p className="text-[13px] text-clay" data-id="element-6729" data-mp="347qpp0l2veuj">{product.brand}</p>
              <h3 className="mt-0.5 font-display text-[1.05rem] leading-snug text-ink" data-id="element-6730" data-mp="396jxccdoobms">{product.name}</h3>
              {meta && <p className="mt-0.5 text-[13px] text-muted" data-id="element-6731" data-mp="3d7n84kgvjhh5">{meta}</p>}
            </div>
            <button type="button" onClick={() => remove(product.id)} aria-label={shopLabels.remove(product)} className={`-mr-2 -mt-1 ${stepBtn}`} data-id="element-6732" data-mp="3hz5btgaa7w5t">
              <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6733" data-mp="3mxgkhnouc0mg" />
            </button>
          </div>

          {routineId && <p className="mt-2 w-fit bg-blush px-2 py-1 text-[13px] text-ink" data-id="element-6734" data-mp="3rv8aud9u7eoj">Onderdeel van je protocol</p>}
          {soldOut && <p className="mt-2 text-[13px] leading-snug text-clay" data-id="element-6735" data-mp="1l9c2b76zpv6k">
              Inmiddels uitverkocht.{' '}
              <button type="button" onClick={() => remove(product.id)} className="text-ink underline underline-offset-4" data-id="element-6736" data-mp="1g9g63u4jb81k">
                Weghalen<span className="sr-only" data-id="element-6737" data-mp="1bb4vhh6f0tia">: {product.name}</span>
              </button>{' '}
              of{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-6738" data-mp="16dmovxfveqdg">
                app {founder.firstName}
                <span className="sr-only" data-id="element-6739" data-mp="1z18t97wx9z1d">{shopLabels.viaWhatsApp}</span>
              </a>{' '}
              voor een alternatief.
            </p>}

          <div className="mt-auto flex items-center justify-between pt-3" data-id="element-6740" data-mp="1pae5u07xwtec">
            <div className="flex items-center border border-line" data-id="element-6741" data-mp="1p8bdvan88q6z">
              {/* Bij 1 haalt "min" het product weg: dan zegt de knop dat ook. */}
              <button type="button" aria-label={qty <= 1 ? shopLabels.remove(product) : shopLabels.fewer(product)}
            // Bij 1 via remove: dan is er ook "Ongedaan maken".
            onClick={() => qty <= 1 ? remove(product.id) : setQty(product.id, qty - 1)} className={stepBtn} data-id="element-6742" data-mp="2hytd20kty7f4">
                <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6743" data-mp="1xhz0nbcr68yw" />
              </button>
              <span className="min-w-[1.75rem] text-center text-[14px] text-ink" aria-live="polite" data-id="element-6744" data-mp="2l7te9v4wc1k7">
                <span aria-hidden="true" data-id="element-6745" data-mp="2q6cztufkvbc1">{qty}</span>
                <span className="sr-only" data-id="element-6746" data-mp="2q5uveairhqt1">{shopLabels.quantity(product, qty)}</span>
              </span>
              <button type="button" aria-label={shopLabels.more(product)} onClick={() => setQty(product.id, qty + 1)} disabled={soldOut || qty >= maxQty(product)} className={`${stepBtn} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-6747" data-mp="2bhgkrdf3sy1x">
                <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6748" data-mp="xhvjg0udcdpq" />
              </button>
            </div>
            <span className="font-display text-lg text-ink" data-id="element-6749" data-mp="sjtxn85k60ak">{euro(product.price * qty)}</span>
          </div>
          {/* Waarom "+" uit staat: anders lijkt de knop kapot. */}
          {!soldOut && qty >= maxQty(product) && <p className="mt-1.5 text-[13px] text-muted" data-id="element-6750" data-mp="12a6i70ddhtgp">Meer hebben we nu niet op voorraad.</p>}
        </div>
      </div>

      {canSubscribe(features, product) && <label className="mt-3 flex cursor-pointer items-start gap-3 bg-sand p-3" data-id="element-6751" data-mp="9kptodsviwpr">
          <input type="checkbox" checked={subscribe} onChange={() => toggleSubscribe(product.id)} className="mt-0.5 h-4 w-4 shrink-0 accent-clay" data-id="element-6752" data-mp="njz755pp3cq1" />
          <span className="text-[13px] leading-snug text-muted" data-id="element-6753" data-mp="jhktd00vv35m">
            <span className="flex items-center gap-1.5 text-ink" data-id="element-6754" data-mp="1yyl6by0xapf5">
              <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6755" data-mp="1z0z083jv9vl4" />
              Automatisch bijvullen
            </span>
            Zelfde prijs, {policyCopy.subscriptionPerks}.
          </span>
        </label>}
    </motion.li>;
}